import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./CustomerSupport.css";
import {
    customerSupportApi,
    PRIORITY_CHIP_CLS,
    PRIORITY_DOT,
    STATUS_CHIP_CLS,
    STATUS_DOT,
    SEVERITY_DOT,
    SEVERITY_SHORT,
    initials,
    avatarGradient,
    relativeTime,
} from "./customerSupportApi";
import { ProductPicker, useCsProduct } from "./ProductPicker";

/* ── Static lookups (presentation only) ── */
const STATUS_COLORS = {
    Open: "#3b82f6",
    "In Progress": "#eab308",
    "Awaiting User": "#ec4899",
    Resolved: "#22c55e",
    Closed: "#666880",
};
const STATUS_ORDER = ["Open", "In Progress", "Awaiting User", "Resolved", "Closed"];

const PRIORITY_COLORS = {
    Critical: "#f87171",
    High: "#fb923c",
    Medium: "#facc15",
    Low: "#4ade80",
};
const PRIORITY_GRAD = {
    Critical: "linear-gradient(90deg,#ef4444,#f87171)",
    High: "linear-gradient(90deg,#f97316,#fb923c)",
    Medium: "linear-gradient(90deg,#eab308,#facc15)",
    Low: "linear-gradient(90deg,#22c55e,#4ade80)",
};
const PRIORITY_ORDER = ["Critical", "High", "Medium", "Low"];

const CAT_PALETTE = [
    { color: "#f87171", grad: "linear-gradient(90deg,#ef4444,#f87171)" },
    { color: "#4ade80", grad: "linear-gradient(90deg,#22c55e,#4ade80)" },
    { color: "#9b80ff", grad: "linear-gradient(90deg,#7c5cff,#9b80ff)" },
    { color: "#fb923c", grad: "linear-gradient(90deg,#f97316,#fb923c)" },
    { color: "#90b8ff", grad: "linear-gradient(90deg,#3b82f6,#90b8ff)" },
    { color: "#f9a8d4", grad: "linear-gradient(90deg,#ec4899,#f9a8d4)" },
    { color: "#5eead4", grad: "linear-gradient(90deg,#04b7b1,#5eead4)" },
    { color: "#a0a5b8", grad: "linear-gradient(90deg,#666880,#a0a5b8)" },
];

const RANK_CLS = { 1: "r1", 2: "r2", 3: "r3" };

const showToast = (msg) => {
    let t = document.getElementById("cs-toast");
    if (!t) {
        t = document.createElement("div");
        t.id = "cs-toast";
        t.className = "cs-toast";
        document.body.appendChild(t);
    }
    t.textContent = msg;
    requestAnimationFrame(() => t.classList.add("show"));
    clearTimeout(t._hide);
    t._hide = setTimeout(() => t.classList.remove("show"), 2000);
};

/* ── Donut math: given a list of {value} produce stroke-dasharray segments ── */
const donutSegments = (items, circumference) => {
    const total = items.reduce((s, x) => s + (x.value || 0), 0) || 1;
    let offset = 0;
    return items.map((it) => {
        const len = (it.value / total) * circumference;
        const segment = {
            stroke: it.color,
            dashArray: `${len} ${circumference - len}`,
            dashOffset: -offset,
        };
        offset += len;
        return segment;
    });
};

/* ── Date-range presets — same labels as the Tickets page ── */
const DATE_PRESETS = [
    { value: "today", label: "Today" },
    { value: "7d",    label: "Last 7 days" },
    { value: "30d",   label: "Last 30 days" },
    { value: "90d",   label: "Last 90 days" },
    { value: "all",   label: "All time" },
];
const DATE_PRESET_LABEL = Object.fromEntries(DATE_PRESETS.map((p) => [p.value, p.label]));

const CustomerSupportDashboard = () => {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [syncing, setSyncing] = useState(false);

    /* Product (eMedHub / Ayurveda) — shared with the Tickets page. */
    const { product, setProduct, products, allProductsLabel } = useCsProduct();

    /* Date-range state — drives the dashboard fetch params. */
    const [datePreset, setDatePreset] = useState("30d");
    const [customDate, setCustomDate] = useState({ from: "", to: "" });
    const [showDateDD, setShowDateDD] = useState(false);

    /* Click outside the date dropdown closes it. */
    useEffect(() => {
        const handler = (e) => {
            if (!e.target.closest(".dropdown") && !e.target.closest("[data-date-picker]")) {
                setShowDateDD(false);
            }
        };
        document.addEventListener("click", handler);
        return () => document.removeEventListener("click", handler);
    }, []);

    const handleSync = async () => {
        if (syncing) return;
        setSyncing(true);
        try {
            const res = await customerSupportApi.triggerSync({ refresh_registry: true });
            showToast(res.message || "Sync complete");
            setRefreshKey((k) => k + 1);
        } catch (e) {
            showToast(e.message || "Sync failed");
        } finally {
            setSyncing(false);
        }
    };

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        // Build params: explicit range wins, otherwise the chosen preset.
        const params = customDate.from || customDate.to
            ? { date_from: customDate.from || undefined, date_to: customDate.to || undefined }
            : { preset: datePreset };
        params.product = product;
        customerSupportApi
            .getDashboard(params)
            .then((res) => {
                if (cancelled) return;
                setData(res.data || res);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err.message || "Failed to load dashboard");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [refreshKey, datePreset, customDate.from, customDate.to, product]);

    /* ── Derived / shaped data ── */
    const summary = data?.summary || {};
    const statusDist = useMemo(() => {
        const m = {};
        (data?.status_distribution || []).forEach((r) => (m[r.status] = r.count));
        return STATUS_ORDER.map((s) => ({
            name: s,
            color: STATUS_COLORS[s],
            value: m[s] || 0,
        }));
    }, [data]);

    const totalStatus = statusDist.reduce((s, x) => s + x.value, 0) || 0;
    const donutSegs = useMemo(() => donutSegments(statusDist, 2 * Math.PI * 70), [statusDist]);

    const priorityDist = useMemo(() => {
        const m = {};
        (data?.priority_distribution || []).forEach((r) => (m[r.priority] = r.count));
        const total = Object.values(m).reduce((s, x) => s + x, 0) || 1;
        return PRIORITY_ORDER.map((p) => ({
            name: p,
            count: m[p] || 0,
            color: PRIORITY_COLORS[p],
            grad: PRIORITY_GRAD[p],
            pct: Math.round(((m[p] || 0) / total) * 100),
        }));
    }, [data]);

    /* Severity mix + measured turnaround against target. These come from
       `fixdesk_severity`, NOT from `priority` above: priority is what the
       customer claimed, severity is what triage agreed, and only the second
       has a turnaround target attached to it. */
    const severityRows = useMemo(() => {
        const counts = {};
        (data?.severity_distribution || []).forEach((r) => (counts[r.severity] = r.count));
        const tat = {};
        (data?.tat_by_severity || []).forEach((r) => (tat[r.severity] = r));
        const total = Object.values(counts).reduce((a, b) => a + b, 0) || 1;
        return ["P0", "P1", "P2", "P3"].map((sev) => ({
            sev,
            count: counts[sev] || 0,
            pct: Math.round(((counts[sev] || 0) / total) * 100),
            color: SEVERITY_DOT[sev],
            ...(tat[sev] || {}),
        }));
    }, [data]);

    const untriagedCount = useMemo(
        () => (data?.severity_distribution || []).find((r) => r.severity === "Untriaged")?.count || 0,
        [data]
    );

    const categoryDist = useMemo(() => {
        const rows = (data?.category_distribution || []).filter((r) => r.category__name);
        const max = Math.max(1, ...rows.map((r) => r.count));
        return rows.map((r, i) => {
            const pal = CAT_PALETTE[i % CAT_PALETTE.length];
            return {
                name: r.category__name,
                count: r.count,
                color: r.category__color_code || pal.color,
                grad: pal.grad,
                pct: Math.round((r.count / max) * 100),
            };
        });
    }, [data]);

    const topClients = useMemo(() => {
        const rows = data?.top_customers || [];
        if (!rows.length) return [];
        const max = rows[0].count || 1;
        return rows.slice(0, 3).map((r, i) => ({
            rank: i + 1,
            rankCls: RANK_CLS[i + 1] || "",
            name: r.customer__customer_name,
            pill: r.customer__customer_code,
            meta: [r.customer__department, r.customer__city].filter(Boolean).join(" · ") || "—",
            count: r.count,
            barPct: Math.round((r.count / max) * 100),
        }));
    }, [data]);

    const recentTickets = data?.recent_tickets || [];

    const agentPerf = useMemo(() => {
        const rows = data?.agent_performance || [];
        const max = Math.max(1, ...rows.map((r) => r.resolved || 0));
        return rows.slice(0, 5).map((r, i) => {
            const name = r.assigned_to__name || "Unassigned";
            return {
                rank: i + 1,
                top: i < 3,
                initials: initials(name),
                grad: avatarGradient(r.assigned_to__id || name),
                name,
                resolved: r.resolved || 0,
                avg: r.avg_hours ? `${r.avg_hours}h` : "—",
                avgCls: "var(--cs-good-text)",
                barPct: Math.round(((r.resolved || 0) / max) * 100),
            };
        });
    }, [data]);

    /* Trend chart point coordinates */
    const trend = useMemo(() => {
        const rows = data?.trend_14d || [];
        if (!rows.length) return null;
        const maxY = Math.max(1, ...rows.map((r) => Math.max(r.created || 0, r.resolved || 0))) * 1.1;
        const xStep = 660 / Math.max(1, rows.length - 1);
        const yScale = (v) => 160 - ((v || 0) / maxY) * 140;
        const path = (key) =>
            rows
                .map((r, i) => `${i === 0 ? "M" : "L"} ${30 + i * xStep},${yScale(r[key])}`)
                .join(" ");
        const areaPath = (key) =>
            `${rows
                .map((r, i) => `${i === 0 ? "M" : "L"} ${30 + i * xStep},${yScale(r[key])}`)
                .join(" ")} L ${30 + (rows.length - 1) * xStep},160 L 30,160 Z`;
        const points = (key) =>
            rows.map((r, i) => ({ cx: 30 + i * xStep, cy: yScale(r[key]) }));

        const avgCreated = rows.reduce((s, r) => s + (r.created || 0), 0) / rows.length;
        const avgResolved = rows.reduce((s, r) => s + (r.resolved || 0), 0) / rows.length;

        return {
            rows,
            yLabels: [maxY, maxY * 0.75, maxY * 0.5, maxY * 0.25, 0].map((v) => Math.round(v)),
            createdPath: path("created"),
            resolvedPath: path("resolved"),
            createdArea: areaPath("created"),
            resolvedArea: areaPath("resolved"),
            createdPoints: points("created"),
            resolvedPoints: points("resolved"),
            avgCreated: avgCreated.toFixed(1),
            avgResolved: avgResolved.toFixed(1),
            xStep,
        };
    }, [data]);

    /* ── Header config ── */
    const HEADER_STATS = [
        { cls: "s-purple", num: summary.total ?? 0, lbl: "Total Tickets", icon: (
            <svg viewBox="0 0 24 24" fill="none" strokeWidth="2"><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></svg>
        ) },
        { cls: "s-blue", num: summary.open ?? 0, lbl: "Open", icon: (
            <svg viewBox="0 0 24 24" fill="none" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
        ) },
        { cls: "s-orange", num: summary.in_progress ?? 0, lbl: "In Progress", icon: (
            <svg viewBox="0 0 24 24" fill="none" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>
        ) },
        { cls: "s-green", num: summary.resolved ?? 0, lbl: "Resolved", icon: (
            <svg viewBox="0 0 24 24" fill="none" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>
        ) },
        { cls: "s-red", num: summary.sla_breach ?? 0, lbl: "SLA Breach", icon: (
            <svg viewBox="0 0 24 24" fill="none" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
        ) },
        // { cls: "s-teal", numHtml: (
        //     <>
        //         {data?.kpis?.csat_avg != null ? data.kpis.csat_avg : "—"}{" "}
        //         <span style={{ fontSize: 13, color: "var(--cs-text-dim)" }}>/ 5</span>
        //     </>
        // ), lbl: "CSAT Score", icon: (
        //     <svg viewBox="0 0 24 24" fill="none" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M8 14s1.5 2 4 2 4-2 4-2" /><line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" /></svg>
        // ) },
    ];

    const kpis = data?.kpis || {};
    const KPI_TILES = [
        // Median, not mean. A handful of tickets nobody answered for a week
        // drag the average somewhere no real ticket sits — and the mean is
        // what made "first response" unusable as a number before.
        { lbl: "First Response", val: kpis.first_response_median_minutes != null ? String(kpis.first_response_median_minutes) : "—", unit: "min (median)", delta: "", deltaCls: "up" },
        { lbl: "Avg Resolution", val: kpis.avg_resolution_hours != null ? String(kpis.avg_resolution_hours) : "—", unit: "hrs", delta: "", deltaCls: "up" },
        { lbl: "Reopened Rate",  val: kpis.reopen_rate_pct != null ? String(kpis.reopen_rate_pct) : "—", unit: "%", delta: "", deltaCls: "down" },
        // How often the team moved a ticket BELOW the tier the customer's own
        // priority implied. A high number is the evidence that `priority`
        // could never have carried a turnaround target.
        { lbl: "Priority Downgraded", val: kpis.severity_downgrade_pct != null ? String(kpis.severity_downgrade_pct) : "—", unit: "%", delta: "", deltaCls: "down" },
        // { lbl: "CSAT",           val: kpis.csat_avg != null ? String(kpis.csat_avg) : "—", unit: "/ 5", delta: "", deltaCls: "up" },
    ];

    /* Hero average completion */
    const avgCompletion = summary.avg_completion_hours;

    /* ── Render ── */
    return (
        <div className="cs-page">
            {/* Page header */}
            <div className="page-hdr">
                <div>
                    <div className="page-crumb">
                        Customer Support <span className="sep">›</span> <span className="crumb-current">Dashboard</span>
                    </div>
                    <div className="page-title">Support Overview</div>
                </div>
                {/* Product picker — every number below, the 14-day trend
                    included, is for the chosen product only. */}
                <div style={{ marginLeft: "auto", marginRight: 8 }}>
                    <ProductPicker product={product} products={products} onChange={setProduct} />
                </div>
                {/* Date-range picker — drives every metric except the
                    14-day trend chart (which is intentionally fixed). */}
                <div className="filter-group" data-date-picker style={{ marginRight: 8 }}>
                    <button
                        className={`filter-select ${datePreset !== "30d" || customDate.from || customDate.to ? "active" : ""}`}
                        onClick={(e) => { e.stopPropagation(); setShowDateDD((v) => !v); }}
                    >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: .8 }}>
                            <rect x="3" y="4" width="18" height="18" rx="2" />
                            <line x1="16" y1="2" x2="16" y2="6" />
                            <line x1="8" y1="2" x2="8" y2="6" />
                            <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                        <span className="lbl-dim">Date:</span>{" "}
                        <span className="val">
                            {(customDate.from || customDate.to)
                                ? `${customDate.from || "…"} → ${customDate.to || "…"}`
                                : (DATE_PRESET_LABEL[datePreset] || "Last 30 days")}
                        </span>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="6 9 12 15 18 9" />
                        </svg>
                    </button>
                    {showDateDD && (
                        <div className="dropdown dd-date show" onClick={(e) => e.stopPropagation()} style={{ right: 0, left: "auto" }}>
                            <div className="dd-header">Quick ranges</div>
                            <div className="dd-date-presets">
                                {DATE_PRESETS.map((p) => (
                                    <div
                                        key={p.value}
                                        className={`dd-option ${datePreset === p.value && !customDate.from && !customDate.to ? "selected" : ""}`}
                                        onClick={() => {
                                            setDatePreset(p.value);
                                            setCustomDate({ from: "", to: "" });
                                            setShowDateDD(false);
                                        }}
                                    >
                                        <span className="dd-label">{p.label}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="dd-divider"></div>
                            <div className="dd-header">Custom range</div>
                            <div className="dd-date-inputs">
                                <div>
                                    <label>From</label>
                                    <input
                                        type="date"
                                        value={customDate.from}
                                        onChange={(e) => setCustomDate({ ...customDate, from: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label>To</label>
                                    <input
                                        type="date"
                                        value={customDate.to}
                                        onChange={(e) => setCustomDate({ ...customDate, to: e.target.value })}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <span className="live">
                    <span className="live-dot"></span>
                    Live
                </span>
                <button className="filter-btn" onClick={() => setRefreshKey((k) => k + 1)} title="Refresh">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" /></svg>
                    Refresh
                </button>
                <button className="filter-btn primary" onClick={handleSync} disabled={syncing}
                    title={`Pull new tickets from ${allProductsLabel}`}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" /></svg>
                    {syncing ? "Syncing…" : `Sync from ${allProductsLabel}`}
                </button>
            </div>

            {error && (
                <div className="panel" style={{ borderColor: "rgba(239,68,68,.4)", color: "var(--cs-bad-text)", marginBottom: 16 }}>
                    {error}
                </div>
            )}

            {/* Stat cards */}
            <div className="stats-grid">
                {HEADER_STATS.map((s, i) => (
                    <div key={i} className={`stat-card ${s.cls}`}>
                        <div className="stat-icon">{s.icon}</div>
                        <div>
                            <div className="stat-num">{loading ? "—" : (s.numHtml || s.num)}</div>
                            <div className="stat-lbl">{s.lbl}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Hero */}
            <div className="hero-metric">
                <div className="hero-big">
                    <div className="hero-eyebrow">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                        Avg Ticket Completion Time
                    </div>
                    <div className="hero-num">
                        {avgCompletion != null ? avgCompletion : "—"}
                        <span className="unit">hrs</span>
                    </div>
                    <div className="hero-sub">
                        Based on {summary.resolved ?? 0} resolved tickets
                    </div>
                </div>
                <div className="hero-breakdown">
                    {PRIORITY_ORDER.map((p) => {
                        const row = priorityDist.find((x) => x.name === p) || { count: 0 };
                        return (
                            <div key={p} className="hb-item" style={{ "--c": PRIORITY_COLORS[p] }}>
                                <div className="hb-label"><span className="dot" style={{ background: PRIORITY_COLORS[p] }}></span>{p}</div>
                                <div className="hb-val">{row.count}<span className="unit">tickets</span></div>
                                <div className="hb-sub">{row.pct}% of total</div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Status donut + categories + SLA / priority */}
            <div className="row r-3">
                <div className="panel">
                    <div className="panel-hdr">
                        <svg viewBox="0 0 24 24" fill="none" strokeWidth="2"><path d="M20 7L9 18l-5-5" /></svg>
                        <span className="panel-title">Status Distribution</span>
                        <span className="panel-sub">{totalStatus} total</span>
                    </div>
                    <div className="donut-wrap">
                        <div className="donut">
                            <svg width="180" height="180" viewBox="0 0 180 180">
                                {donutSegs.map((seg, i) => (
                                    <circle
                                        key={i}
                                        cx="90" cy="90" r="70"
                                        fill="none"
                                        stroke={seg.stroke}
                                        strokeWidth="22"
                                        strokeDasharray={seg.dashArray}
                                        strokeDashoffset={seg.dashOffset}
                                    />
                                ))}
                            </svg>
                            <div className="donut-center">
                                <div className="donut-num">{totalStatus}</div>
                                <div className="donut-lbl">Tickets</div>
                            </div>
                        </div>
                        <div className="legend">
                            {statusDist.map((s) => {
                                const pct = totalStatus ? Math.round((s.value / totalStatus) * 100) : 0;
                                return (
                                    <div key={s.name} className="lg-row">
                                        <span className="lg-dot" style={{ background: s.color }}></span>
                                        <span className="lg-name">{s.name}</span>
                                        <div className="lg-bar"><div className="lg-bar-fill" style={{ width: `${pct}%`, background: s.color }}></div></div>
                                        <span className="lg-val">{s.value}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="panel">
                    <div className="panel-hdr">
                        <svg viewBox="0 0 24 24" fill="none" strokeWidth="2"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>
                        <span className="panel-title">Tickets by Category</span>
                        <span className="panel-sub">{categoryDist.length} categories</span>
                    </div>
                    {categoryDist.length === 0 ? (
                        <div style={{ padding: 32, color: "var(--cs-text-dim)", fontSize: 12, textAlign: "center" }}>
                            No category data yet
                        </div>
                    ) : (
                        <div className="cat-bar-list">
                            {categoryDist.map((c, i) => (
                                <div key={i} className="cat-bar-row">
                                    <div className="cat-bar-name"><span className="dot" style={{ background: c.color }}></span>{c.name}</div>
                                    <div className="cat-bar-cnt">{c.count}</div>
                                    <div className="cat-bar-track"><div className="cat-bar-fill" style={{ width: `${c.pct}%`, background: c.grad }}></div></div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="panel">
                    <div className="panel-hdr">
                        <svg viewBox="0 0 24 24" fill="none" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                        <span className="panel-title">SLA Compliance</span>
                    </div>
                    {(() => {
                        const total = summary.total || 0;
                        const breached = summary.sla_breach || 0;
                        const pct = total ? Math.max(0, Math.round(((total - breached) / total) * 100)) : 100;
                        const isHealthy = pct >= 90;
                        return (
                            <div className="gauge-wrap">
                                <div className="gauge">
                                    <svg width="200" height="120" viewBox="0 0 200 120">
                                        <path className="gauge-track" d="M 20 110 A 80 80 0 0 1 180 110" fill="none" strokeWidth="14" strokeLinecap="round" />
                                        <path
                                            d="M 20 110 A 80 80 0 0 1 180 110"
                                            fill="none"
                                            stroke={isHealthy ? "#22c55e" : "#facc15"}
                                            strokeWidth="14"
                                            strokeLinecap="round"
                                            strokeDasharray={`${(pct / 100) * 251.3} 251.3`}
                                        />
                                    </svg>
                                    <div className="gauge-val">
                                        <div className="gauge-num">{pct}%</div>
                                        <div className="gauge-lbl">On Time</div>
                                    </div>
                                </div>
                                <div className="gauge-tag">{isHealthy ? "Healthy" : "At Risk"}</div>
                            </div>
                        );
                    })()}

                    <div className="priority-wrap">
                        <div className="priority-title">Priority Breakdown</div>
                        <div className="priority-list">
                            {priorityDist.map((p) => (
                                <div key={p.name} className="pr-row">
                                    <div className="pr-name-w"><span className="pr-dot-sm" style={{ background: p.color }}></span>{p.name}</div>
                                    <div className="pr-bar"><div className="pr-bar-fill" style={{ width: `${p.pct}%`, background: p.grad }}></div></div>
                                    <div className="pr-cnt">{p.count}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Trend + Top clients + recent */}
            {/* ── Turnaround by severity ──
                The card that answers "how long is support taking". Two numbers
                per tier: the median (a typical ticket) and p90 (the tail). The
                mean is deliberately absent — a handful of tickets that sat open
                for three weeks drag it somewhere no real ticket sits. */}
            <div className="row">
                <div className="panel">
                    <div className="panel-hdr">
                        <svg viewBox="0 0 24 24" fill="none" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                        <span className="panel-title">Turnaround by Severity</span>
                        <span className="panel-sub">support time only — working hours, customer wait excluded</span>
                    </div>

                    <div className="sev-tat-table">
                        <div className="sev-tat-head">
                            <span>TIER</span>
                            <span>TICKETS</span>
                            <span>TYPICAL</span>
                            <span>SLOWEST 10%</span>
                            <span>TARGET</span>
                            <span>MET</span>
                        </div>

                        {severityRows.map((r) => {
                            const met = r.within_target_pct;
                            const metCls = met == null ? "" : met >= 90 ? "good" : met >= 70 ? "warn" : "bad";
                            return (
                                <div key={r.sev} className="sev-tat-row">
                                    <span
                                        className={`sev-chip sev-${r.sev.toLowerCase()}`}
                                        title={`${r.sev} — ${SEVERITY_SHORT[r.sev]}`}
                                    >
                                        <span className="chip-dot" style={{ background: r.color }}></span>
                                        {r.sev}
                                    </span>
                                    <span className="sev-tat-n" title={`${r.count} tickets in this window`}>
                                        {r.count}
                                        {r.resolved_count ? <small> · {r.resolved_count} resolved</small> : null}
                                    </span>
                                    <span className="sev-tat-v">{r.p50_display || "—"}</span>
                                    <span className="sev-tat-v dim">{r.p90_display || "—"}</span>
                                    <span className="sev-tat-v dim">{r.target_display || "not set"}</span>
                                    <span className={`sev-tat-met ${metCls}`}>
                                        {met == null ? "—" : `${met}%`}
                                    </span>
                                </div>
                            );
                        })}

                        {/* Untriaged is stated, never folded into the tiers above.
                            These tickets have no target, so they are absent from
                            every percentage on this card — a report that quietly
                            drops rows reads as complete when it isn't. */}
                        {untriagedCount > 0 && (
                            <div className="sev-tat-note">
                                <strong>{untriagedCount}</strong> ticket{untriagedCount === 1 ? " has" : "s have"} no
                                severity set, so no target applies and {untriagedCount === 1 ? "it is" : "they are"} not
                                counted above.
                                <button className="linkish" onClick={() => navigate("/customer-support/tickets")}>
                                    Triage them →
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="row dash-r2">
                <div className="panel">
                    <div className="panel-hdr">
                        <svg viewBox="0 0 24 24" fill="none" strokeWidth="2"><polyline points="3 17 9 11 13 15 21 7" /><polyline points="14 7 21 7 21 14" /></svg>
                        <span className="panel-title">Ticket Volume — Last 14 Days</span>
                        <span className="panel-sub">Created vs Resolved</span>
                    </div>
                    <div className="trend-wrap">
                        <div className="trend-legend">
                            <span><span className="swatch" style={{ background: "linear-gradient(180deg,rgba(124,92,255,.6),rgba(124,92,255,.1))", border: "1px solid var(--accent)" }}></span> Created</span>
                            <span><span className="swatch" style={{ background: "linear-gradient(180deg,rgba(34,197,94,.6),rgba(34,197,94,.1))", border: "1px solid #22c55e" }}></span> Resolved</span>
                            <span className="avg">
                                Avg/day: {trend?.avgCreated ?? "—"} created · {trend?.avgResolved ?? "—"} resolved
                            </span>
                        </div>
                        {trend ? (
                            <svg className="trend-svg" viewBox="0 0 700 180" preserveAspectRatio="none">
                                <g className="trend-grid">
                                    <line x1="30" y1="20" x2="690" y2="20" />
                                    <line x1="30" y1="55" x2="690" y2="55" />
                                    <line x1="30" y1="90" x2="690" y2="90" />
                                    <line x1="30" y1="125" x2="690" y2="125" />
                                    <line x1="30" y1="160" x2="690" y2="160" />
                                </g>
                                <g className="trend-y-axis">
                                    <text x="22" y="23" textAnchor="end">{trend.yLabels[0]}</text>
                                    <text x="22" y="58" textAnchor="end">{trend.yLabels[1]}</text>
                                    <text x="22" y="93" textAnchor="end">{trend.yLabels[2]}</text>
                                    <text x="22" y="128" textAnchor="end">{trend.yLabels[3]}</text>
                                    <text x="22" y="163" textAnchor="end">{trend.yLabels[4]}</text>
                                </g>
                                <path d={trend.createdArea} fill="rgba(124,92,255,.18)" stroke="none" />
                                <path d={trend.createdPath} fill="none" stroke="#9b80ff" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                                <path d={trend.resolvedArea} fill="rgba(34,197,94,.14)" stroke="none" />
                                <path d={trend.resolvedPath} fill="none" stroke="#4ade80" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                                <g fill="#9b80ff">{trend.createdPoints.map((p, i) => <circle key={i} cx={p.cx} cy={p.cy} r="3" />)}</g>
                                <g fill="#4ade80">{trend.resolvedPoints.map((p, i) => <circle key={i} cx={p.cx} cy={p.cy} r="3" />)}</g>
                                <g className="trend-x-axis">
                                    {trend.rows.map((r, i) =>
                                        i === trend.rows.length - 1
                                            ? <text key={i} x={30 + i * trend.xStep} y="178" textAnchor="middle">Today</text>
                                            : (i % 2 === 0 && <text key={i} x={30 + i * trend.xStep} y="178" textAnchor="middle">{trend.rows.length - i - 1}</text>)
                                    )}
                                </g>
                            </svg>
                        ) : (
                            <div style={{ padding: 32, color: "var(--cs-text-dim)", fontSize: 12, textAlign: "center" }}>
                                No trend data yet
                            </div>
                        )}
                    </div>
                </div>

                <div className="panel">
                    <div className="panel-hdr">
                        <svg viewBox="0 0 24 24" fill="none" strokeWidth="2"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" /><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" /><path d="M4 22h16" /><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" /><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" /><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" /></svg>
                        <span className="panel-title">Top 3 Clients by Volume</span>
                    </div>
                    {topClients.length === 0 ? (
                        <div style={{ padding: 24, color: "var(--cs-text-dim)", fontSize: 12, textAlign: "center" }}>
                            No customer data yet
                        </div>
                    ) : (
                        <div className="client-list">
                            {topClients.map((c) => (
                                <div key={c.rank} className={`client-row ${c.rankCls}`}>
                                    <div className="client-rank">{c.rank}</div>
                                    <div className="client-info">
                                        <div className="client-name">{c.name}</div>
                                        <div className="client-meta">
                                            <span>DB id</span>
                                            <span className="ms-pill">{c.pill}</span>
                                        </div>
                                    </div>
                                    <div className="client-cnt">
                                        <div className="client-cnt-num">{c.count}</div>
                                        <div className="client-cnt-lbl">tickets</div>
                                    </div>
                                    <div className="client-bar"><div className="client-bar-fill" style={{ width: `${c.barPct}%` }}></div></div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="panel-hdr" style={{ marginTop: 24, paddingTop: 18, borderTop: "1px solid var(--cs-border)" }}>
                        <svg viewBox="0 0 24 24" fill="none" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>
                        <span className="panel-title">Last 5 Recent Tickets</span>
                        <a className="view-all-link" onClick={(e) => { e.preventDefault(); navigate("/customer-support/tickets"); }}>View all →</a>
                    </div>
                    {recentTickets.length === 0 ? (
                        <div style={{ padding: 24, color: "var(--cs-text-dim)", fontSize: 12, textAlign: "center" }}>
                            No tickets yet
                        </div>
                    ) : (
                        <div className="recent-list">
                            {recentTickets.map((t) => (
                                <div key={t.id} className="recent-item" onClick={() => navigate("/customer-support/tickets")}>
                                    <span className="recent-pid">{t.ticket_code}</span>
                                    <div className="recent-body">
                                        <div className="recent-sub">{t.subject}</div>
                                        <div className="recent-meta">
                                            <span className={`pr-chip ${PRIORITY_CHIP_CLS[t.priority]} chip-mini`}>
                                                <span className="chip-dot" style={{ background: PRIORITY_DOT[t.priority] }}></span>{t.priority}
                                            </span>
                                            <span className="dot-sep"></span>
                                            <span>{t.customer_name}</span>
                                        </div>
                                    </div>
                                    <span className={`chip ${STATUS_CHIP_CLS[t.status]} chip-mini-st`}>
                                        <span className="chip-dot" style={{ background: STATUS_DOT[t.status] }}></span>{t.status}
                                    </span>
                                    <span className="recent-time">{relativeTime(t.created_time)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Agent leaderboard + KPIs / anomalies */}
            <div className="row dash-r3">
                <div className="panel">
                    <div className="panel-hdr">
                        <svg viewBox="0 0 24 24" fill="none" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                        <span className="panel-title">Agent Performance</span>
                        <span className="panel-sub">this week</span>
                    </div>
                    {agentPerf.length === 0 ? (
                        <div style={{ padding: 24, color: "var(--cs-text-dim)", fontSize: 12, textAlign: "center" }}>
                            No resolved tickets in the last week
                        </div>
                    ) : (
                        <div className="leaderboard">
                            <div className="lb-head">
                                <span>#</span>
                                <span>AGENT</span>
                                <span style={{ textAlign: "right" }}>RESOLVED</span>
                                <span style={{ textAlign: "right" }}>AVG TIME</span>
                                <span style={{ textAlign: "right" }}>SCORE</span>
                            </div>
                            {agentPerf.map((a) => (
                                <div key={a.rank} className={`lb-row ${a.top ? "top" : ""}`}>
                                    <div className="lb-rank">{a.rank}</div>
                                    <div className="lb-name">
                                        <div className="agent-avatar sm" style={{ background: a.grad }}>{a.initials}</div>
                                        {a.name}
                                    </div>
                                    <div className="lb-num">{a.resolved}</div>
                                    <div className="lb-num" style={{ color: a.avgCls }}>{a.avg}</div>
                                    <div className="lb-bar"><div className="lb-bar-track"><div className="lb-bar-fill" style={{ width: `${a.barPct}%` }}></div></div></div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="panel">
                    <div className="panel-hdr">
                        <svg viewBox="0 0 24 24" fill="none" strokeWidth="2"><polyline points="3 17 9 11 13 15 21 7" /><polyline points="14 7 21 7 21 14" /></svg>
                        <span className="panel-title">Performance KPIs</span>
                        <span className="panel-sub">this week</span>
                    </div>

                    <div className="kpi-grid">
                        {KPI_TILES.map((k, i) => (
                            <div key={i} className="kpi-tile">
                                <div className="kpi-tile-lbl">{k.lbl}</div>
                                <div className="kpi-tile-val">{k.val}<span className="unit"> {k.unit}</span></div>
                                {k.delta && <div className={`kpi-tile-delta ${k.deltaCls}`}>{k.delta}</div>}
                            </div>
                        ))}
                    </div>

                    <div className="kpi-section-title">Anomalies & Risks</div>
                    <div className="kpi-items">
                        {(data?.anomalies || []).length === 0 ? (
                            <div className="kpi-item ok">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>
                                <span>No anomalies detected</span>
                            </div>
                        ) : (
                            (data?.anomalies || []).map((a, i) => (
                                <div key={i} className={`kpi-item ${a.severity}`}>
                                    {a.severity === "crit" ? (
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /></svg>
                                    ) : (
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /></svg>
                                    )}
                                    <span>{a.message}</span>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomerSupportDashboard;
